import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { baseGrid } from '@helpers/grid';
import { imageCoverContainer } from '@helpers/image';
import { Device, from, until } from '@helpers/media';
import { contentStyles, h3, paragraphSmall } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import CtaLinkStyles from '@stories/Components/Buttons/CtaLink/CtaLink.styles';
import { ReadMoreStyles } from '@stories/Components/Buttons/ReadMore/ReadMore.styles';
import Image from '@stories/Components/Misc/Image/Image';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import styled, { css } from 'styled-components';
const Container = styled(SectionWrapper) `
  --tagColor: ${brand.primary.orange};
  --imgColor: ${brand.white};

  background-color: var(--bgColor);
  overflow: hidden;

  &[data-border-color='orange'] {
    --imgColor: var(--tagColor);
  }

  &[data-variant='inner'] {
    --bgColor: ${brand.primary.blue};
    --fgColor: ${brand.white};
  }

  &[data-variant='article'] {
    --bgColor: ${brand.secondary.lightGrey};
    --fgColor: ${brand.base.blue};
  }

  &[data-variant='green'] {
    --bgColor: ${brand.secondary.green};
    --fgColor: ${brand.white};
  }

  ${SectionWrapperStyles.Inner} {
    ${baseGrid};
  }

  @media ${from(Device.DesktopSmall)} {
    &[data-layout='default'] {
      ${SectionWrapperStyles.Inner} {
        min-height: ${fluid(300, 600)};
      }
    }
  }
`;
const ContentWrapper = styled.div `
  grid-row: 2;
  grid-column: span 24;
  padding: ${fluid(20, 60)} 0 ${fluid(50, 60)};
  color: var(--fgColor);

  ${Container}[data-layout='center'] & {
    text-align: center;
  }

  @media ${from(Device.Tablet)} {
    ${Container}[data-layout='center'] & {
      grid-column: 3 / span 20;
    }

    ${Container}[data-layout='split'] & {
      ${baseGrid};
    }
  }

  @media ${from(Device.TabletLarge)} {
    grid-row: 1;

    ${Container}[data-layout='center'] & {
      grid-column: 6 / span 14;
    }

    ${Container}[data-layout='default'] & {
      grid-column: span 15;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    ${Container}[data-layout='default'] & {
      grid-column: span 12;
    }
  }

  @media ${from(Device.Desktop)} {
    ${Container}[data-layout='default'] & {
      grid-column: span 10;
    }

    ${Container}[data-layout='center'] & {
      grid-column: 8 / span 10;
    }
  }
`;
const FeaturedTag = styled.span `
  ${fonts.apparat.medium};

  display: inline-block;
  background: ${brand.primary.blue};
  border-radius: 5px;
  color: ${brand.white};
  font-size: ${fluid(14, 18)};
  line-height: 1em;
  margin-bottom: ${fluid(14, 26)};
  padding: 9px ${fluid(14, 16)} 7px;
`;
const Tag = styled.span `
  ${paragraphSmall};

  display: block;
  margin: 0 0 ${fluid(20, 32)};
  text-transform: uppercase;
  color: var(--tagColor);
`;
const Title = styled.h1 `
  ${h3};

  margin: 0 0 ${fluid(18, 32)};

  ${Container}[data-layout='split'] & {
    @media ${from(Device.Tablet)} {
      grid-column: span 9;
    }
  }
`;
const contentSpans = css `
  ${Container}[data-layout='split'] & {
    @media ${from(Device.Tablet)} {
      grid-column: 11 / span 14;
    }

    @media ${from(Device.Desktop)} {
      grid-column: 12 / span 10;
    }
  }
`;
const ContentInner = styled.div `
  ${contentSpans};

  ${ReadMoreStyles.Container} {
    margin-top: 24px;
  }

  ${CtaLinkStyles.Container} {
    font-size: ${fluid(18, 22)};
  }

  @media ${from(Device.TabletLarge)} {
    ${ReadMoreStyles.ReadMore} {
      display: block;
    }
  }
`;
const Content = styled.div `
  ${contentStyles};

  &[data-read-more='closed'] {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    overflow: hidden;

    @media ${from(Device.TabletLarge)} {
      -webkit-line-clamp: 2;
    }
  }

  p {
    font-size: ${fluid(18, 20)};
    line-height: ${fluid(28, 30)};
  }

  ${Container}[data-variant='article'] & {
    p {
      font-size: ${fluid(18, 20)};
      line-height: ${fluid(28, 30)};
    }
  }
`;
const ButtonWrapper = styled.div `
  ${contentSpans};

  display: flex;
  gap: ${fluid(16, 24)};
  flex-wrap: wrap;
  margin: ${fluid(18, 40)} 0 0;

  ${Container}[data-layout='center'] & {
    justify-content: center;
  }

  ${ButtonStyles.Container}[data-is-anchor='true'] {
    ${ButtonStyles.IconWrapper} {
      ${transition('color, transform')};
    }

    &.focus-ring,
    &:hover {
      ${ButtonStyles.IconWrapper} {
        transform: translateY(3px);
      }
    }
  }
`;
const ImageWrapper = styled.div `
  ${transition('transform', 0.75)};

  --imgWidth: calc(100% + var(--sitePadding));
  --borderWidth: ${fluid(3, 8)};

  grid-row: 1;
  grid-column: span 24;
  display: flex;
  position: relative;
  transition-delay: 200ms;

  @media ${from(Device.TabletLarge)} {
    grid-column: 17 / span 8;
  }

  @media ${from(Device.DesktopSmall)} {
    --imgWidth: min(50vw, calc(1920px * 0.5));

    grid-column: 13 / span 12;
    transition-duration: 0.9s;
  }

  @media ${from(Device.Desktop)} {
    transition-duration: 1.2s;
  }

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: calc(var(--imgWidth) + ${fluid(3, 4)});
    height: calc(100% + var(--borderWidth));
    clip-path: url(#inner-hero-clip-path_svg__innerHero);
    background-color: var(--imgColor);
    transform: translate(calc(var(--borderWidth) * -1), calc(var(--borderWidth) * -0.5));
  }

  ${Container}[data-animate='false'] & {
    transform: translateX(90%);
  }
`;
const ImageInner = styled.div `
  ${ButtonReset};

  overflow: hidden;
  position: relative;
  flex: 1 0 var(--imgWidth);
  clip-path: url(#inner-hero-clip-path_svg__innerHero);
  z-index: 1;

  @media ${until(Device.TabletLarge)} {
    aspect-ratio: 363 / 227;
    border-top: 2px solid var(--imgColor);
    border-bottom: 2px solid var(--imgColor);
  }

  @media ${from(Device.TabletLarge)} {
    height: 100%;
  }
`;
const Img = styled(Image) `
  ${imageCoverContainer()};
`;
const PlayIcon = styled.span `
  color: #ff0000;
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: ${fluid(12, 18)};

  > svg {
    display: block;
    width: 68px;
    height: 48px;
  }

  ${ImageInner}.focus-ring & {
    outline: auto;
    outline-offset: 0px;
    outline-style: solid;
    outline-width: 2px;
  }
`;
const InnerPageHeroBannerStyles = {
    ButtonWrapper,
    Container,
    Content,
    ContentInner,
    ContentWrapper,
    FeaturedTag,
    ImageInner,
    ImageWrapper,
    Img,
    Tag,
    Title,
    PlayIcon,
};
export default InnerPageHeroBannerStyles;
